import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from '../context/snackbarContext';
import { useTranslation } from 'react-i18next';

interface PWAInstallState {
    isInstallable: boolean;
    isInstalled: boolean;
    deferredPrompt: any;
    showInstallPrompt: boolean;
    hasShownPrompt: boolean;
    visitCount: number;
}

export const usePWAInstall = () => {
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [state, setState] = useState<PWAInstallState>({
        isInstallable: false,
        isInstalled: false,
        deferredPrompt: null,
        showInstallPrompt: false,
        hasShownPrompt: false,
        visitCount: 0,
    });

    // --- new: platform detection helpers ---
    const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMac = typeof window !== 'undefined' && /Macintosh|MacIntel/.test(navigator.userAgent);
    const isSafari = typeof window !== 'undefined' && /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isIOSSafari = isIOS && isSafari;
    const isMacSafari = isMac && isSafari;

    // Check if app is already installed
    const checkIfInstalled = useCallback(() => {
        // Check if running in standalone mode (installed PWA)
        const isStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches || false;
        // Also check for iOS Safari standalone mode
        const isIOSStandalone = (window.navigator as any)?.standalone === true;

        const isInstalled = isStandalone || isIOSStandalone;

        // Log only in development mode
        if (process.env.NODE_ENV === 'development') {
            console.log('🔍 PWA Installation check:', {
                isStandalone,
                isIOSStandalone,
                isInstalled,
                userAgent: navigator.userAgent
            });
        }

        return isInstalled;
    }, []);

    // Get visit count from localStorage
    const getVisitCount = useCallback(() => {
        const count = localStorage.getItem('pwa_visit_count');
        return count ? parseInt(count, 10) : 0;
    }, []);

    // Increment visit count – only once per page load, even if the hook is mounted multiple times
    const incrementVisitCount = useCallback(() => {
        // Use sessionStorage so the flag resets on full page reload but is shared across React roots/components
        const hasIncrementedThisSession = sessionStorage.getItem('pwa_visit_incremented');

        if (hasIncrementedThisSession) {
            return getVisitCount();
        }

        const currentCount = getVisitCount();
        const newCount = currentCount + 1;
        localStorage.setItem('pwa_visit_count', newCount.toString());
        sessionStorage.setItem('pwa_visit_incremented', 'true');
        return newCount;
    }, [getVisitCount]);

    // Check if we should show the install prompt
    const shouldShowInstallPrompt = useCallback((visitCount: number) => {
        // Show after 2nd visit, but not if user has dismissed it recently
        const dismissedData = localStorage.getItem('pwa_install_dismissed');
        let hasDismissed = false;

        if (dismissedData) {
            try {
                const { timestamp, dismissed } = JSON.parse(dismissedData);
                const now = Date.now();
                const daysSinceDismissed = (now - timestamp) / (1000 * 60 * 60 * 24);

                // Show again after 7 days (configurable)
                const DISMISS_TIMEOUT_DAYS = 7;
                hasDismissed = dismissed && daysSinceDismissed < DISMISS_TIMEOUT_DAYS;

                console.log('🔍 PWA Dismiss check:', {
                    dismissed,
                    daysSinceDismissed: Math.round(daysSinceDismissed * 100) / 100,
                    timeoutDays: DISMISS_TIMEOUT_DAYS,
                    hasDismissed
                });
            } catch (error) {
                // If old format (just "true"), migrate to new format
                if (dismissedData === 'true') {
                    localStorage.setItem('pwa_install_dismissed', JSON.stringify({
                        dismissed: true,
                        timestamp: Date.now()
                    }));
                    hasDismissed = true;
                }
            }
        }

        return visitCount >= 2 && !hasDismissed;
    }, []);

    // Handle the beforeinstallprompt event
    const handleBeforeInstallPrompt = useCallback((e: Event) => {
        // Prevent the mini-infobar from appearing on mobile
        e.preventDefault();

        // Store the event so it can be triggered later
        setState(prev => ({
            ...prev,
            isInstallable: true,
            deferredPrompt: e,
        }));
    }, []);

    // Handle successful installation
    const handleAppInstalled = useCallback(() => {
        console.log('🎉 PWA was installed!');
        setState(prev => ({
            ...prev,
            isInstalled: true,
            isInstallable: false,
            showInstallPrompt: false,
        }));

        // Clear the deferred prompt
        setState(prev => ({ ...prev, deferredPrompt: null }));
    }, []);

    // Trigger the install prompt
    const triggerInstall = useCallback(async () => {
        if (!state.deferredPrompt) {
            console.log('❌ No install prompt available');
            return false;
        }

        try {
            // Show the install prompt
            const result = await state.deferredPrompt.prompt();
            console.log('🎉 Install prompt result:', result);

            // Clear the deferred prompt
            setState(prev => ({ ...prev, deferredPrompt: null }));

            return result.outcome === 'accepted';
        } catch (error) {
            console.error('❌ Error triggering install:', error);
            return false;
        }
    }, [state.deferredPrompt]);

    // Dismiss the install prompt
    const dismissInstallPrompt = useCallback(() => {
        localStorage.setItem('pwa_install_dismissed', JSON.stringify({
            dismissed: true,
            timestamp: Date.now()
        }));
        setState(prev => ({
            ...prev,
            showInstallPrompt: false,
            hasShownPrompt: true,
        }));
    }, []);

    // Show the install prompt
    const showInstallPrompt = useCallback(() => {
        setState(prev => ({
            ...prev,
            showInstallPrompt: true,
            hasShownPrompt: true,
        }));
    }, []);

    // Show snackbar tip for manual installation (always show, no localStorage check)
    const showInstallTip = useCallback(() => {
        const message = isIOSSafari
            ? t('pwa.snackbarTipIOS', '💡 Tip: Install this app on your iPhone: tap the Share icon, then "Add to Home Screen".')
            : isMacSafari
                ? t('pwa.snackbarTipMac', '💡 Tip: Install this app on your Mac: in Safari choose "Add to Dock" from the File menu.')
                : t('pwa.snackbarTip', '💡 Tip: Add this app to your home screen for quick access! Look for "Add to Home Screen" in your browser menu.');

        showSnackbar(message, 'info');
    }, [showSnackbar, t, isIOSSafari, isMacSafari]);

    // Reset dismiss state (for testing)
    const resetDismissState = useCallback(() => {
        console.log('🔍 Hook: Resetting dismiss state');
        localStorage.removeItem('pwa_install_dismissed');
        setState(prev => ({
            ...prev,
            showInstallPrompt: shouldShowInstallPrompt(prev.visitCount),
        }));
    }, [shouldShowInstallPrompt]);

    // Initialize the hook
    useEffect(() => {
        // Skip initialization in test environment
        if (process.env.NODE_ENV === 'test') {
            return;
        }

        const visitCount = incrementVisitCount();
        const isInstalled = checkIfInstalled();
        const shouldShow = shouldShowInstallPrompt(visitCount);

        console.log('🔍 PWA Hook Initialization:', {
            visitCount,
            isInstalled,
            shouldShow,
            hasDismissed: localStorage.getItem('pwa_install_dismissed')
        });

        setState(prev => ({
            ...prev,
            isInstalled,
            visitCount,
            showInstallPrompt: shouldShow,
        }));

        // Note: We'll show the snackbar when user tries to install but can't, not on initialization

        // Add event listeners
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        // Monitor for display mode changes (when app is installed/uninstalled)
        const mediaQuery = window.matchMedia('(display-mode: standalone)');
        const handleDisplayModeChange = () => {
            const newIsInstalled = checkIfInstalled();
            console.log('🔍 PWA Display mode changed:', { newIsInstalled });

            setState(prev => ({
                ...prev,
                isInstalled: newIsInstalled,
                showInstallPrompt: newIsInstalled ? false : shouldShowInstallPrompt(prev.visitCount),
            }));
        };

        mediaQuery.addEventListener('change', handleDisplayModeChange);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
            mediaQuery.removeEventListener('change', handleDisplayModeChange);
        };
    }, [incrementVisitCount, checkIfInstalled, shouldShowInstallPrompt, handleBeforeInstallPrompt, handleAppInstalled, showSnackbar, t, isIOSSafari, isMacSafari]);

    return {
        ...state,
        triggerInstall,
        dismissInstallPrompt,
        showInstallPrompt: state.showInstallPrompt,
        showInstallTip,
        resetDismissState,
    };
};
